import React, { useEffect, useState } from 'react';
import getVisitorLogs from '../../API/getVisitorLogs';
import * as Select from '@radix-ui/react-select';

const VisitorLogs = () => {
  // State to store visitor logs
  const [visitorLogs, setVisitorLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [sortConfig, setSortConfig] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'today', 'thisMonth'
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await getVisitorLogs();
      setVisitorLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      console.error('Error fetching visitor logs:', error);
      setVisitorLogs([]);
      setFilteredLogs([]);
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    // Fetch visitor logs when component mounts
    const fetchData = async () => {
      try {
        const data = await getVisitorLogs();
        setVisitorLogs(data);
        setFilteredLogs(data);
      } catch (error) {
        console.error('Error fetching visitor logs:', error);
        setVisitorLogs([]);
        setFilteredLogs([]);
      }
    };
    fetchData();
  }, []);

  // Function to handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    let sortedLogs = [...visitorLogs];
    if (sortConfig !== null) {
      sortedLogs.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    setFilteredLogs(sortedLogs);
  }, [visitorLogs, sortConfig]);

  // Function to handle filtering
  const handleFilterChange = (value) => {
    setFilter(value);
  };

  useEffect(() => {
    let filtered = [...visitorLogs];
    const today = new Date();
    if (filter === 'today') {
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === today.toDateString();
      });
    } else if (filter === 'thisMonth') {
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.timestamp);
        return (
          logDate.getMonth() === today.getMonth() &&
          logDate.getFullYear() === today.getFullYear()
        );
      });
    }
    setFilteredLogs(filtered);
  }, [filter, visitorLogs]);

  // Calculate counts
  const visitorCountToday = Array.isArray(visitorLogs) ? visitorLogs.filter((log) => {
    const logDate = new Date(log.timestamp);
    const today = new Date();
    return logDate.toDateString() === today.toDateString();
  }).length : 0;

  const visitorCountThisMonth = Array.isArray(visitorLogs) ? visitorLogs.filter((log) => {
    const logDate = new Date(log.timestamp);
    const today = new Date();
    return (
      logDate.getMonth() === today.getMonth() &&
      logDate.getFullYear() === today.getFullYear()
    );
  }).length : 0;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Visitor Logs</h1>
      <div className="mb-4">
        <span className="mr-4">Visitors Today: {visitorCountToday}</span>
        <span>Visitors This Month: {visitorCountThisMonth}</span>
      </div>
      <div className="mb-4">
        <label htmlFor="filter" className="mr-2">
          Filter:
        </label>
        <Select.Root value={filter} onValueChange={handleFilterChange}>
          <Select.Trigger
            className="inline-flex items-center justify-between bg-white border border-gray-300 rounded px-2 py-1"
            aria-label="Filter"
          >
            <Select.Value placeholder="Select filter" />
            <Select.Icon />
          </Select.Trigger>
          <Select.Content className="bg-white border border-gray-300 rounded shadow-lg">
            <Select.Viewport>
              <Select.Item
                value="all"
                className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
              >
                <Select.ItemText>All</Select.ItemText>
              </Select.Item>
              <Select.Item
                value="today"
                className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
              >
                <Select.ItemText>Today</Select.ItemText>
              </Select.Item>
              <Select.Item
                value="thisMonth"
                className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
              >
                <Select.ItemText>This Month</Select.ItemText>
              </Select.Item>
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
      </div>
      <table className="min-w-full border-collapse block md:table">
        <thead className="block md:table-header-group">
          <tr className="border border-gray-500 md:border-none block md:table-row">
            {[
              'id',
              'ip_address',
              'timestamp',
              'country',
              'state',
              'city',
              'path',
              'browser',
              'os',
              'device',
            ].map((key) => (
              <th
                key={key}
                onClick={() => handleSort(key)}
                className="text-left p-2 md:border md:border-gray-500 block md:table-cell cursor-pointer"
              >
                {key.replace('_', ' ').toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="block md:table-row-group">
          {visitorLogs.map((log) => (
            <tr
              key={log.id}
              className="border border-gray-500 md:border-none block md:table-row"
            >
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.id}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.ip_address}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {new Date(log.timestamp).toLocaleString()}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.country || 'N/A'}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.state || 'N/A'}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.city || 'N/A'}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.path}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.browser}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.os}
              </td>
              <td className="p-2 md:border md:border-gray-500 block md:table-cell">
                {log.device}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default VisitorLogs;
